export type ThemeCategoryId =
  | "featured"
  | "dark-moody"
  | "nature-earth"
  | "warm-inviting"
  | "cool-professional";

export type ThemeBadge = "current" | "recommended" | null;

export interface WebsiteThemePreset {
  id: string;
  name: string;
  category: ThemeCategoryId;
  colors: [string, string, string, string];
  primary: string;
  secondary: string;
  sidebar: string;
  surface: string;
  base: string;
  description: string;
  badge?: ThemeBadge;
  isCustom?: boolean;
}

export interface ThemeCategory {
  id: ThemeCategoryId;
  label: string;
}

export const WEBSITE_THEME_STORAGE_KEY = "crm-website-theme";
export const WEBSITE_CUSTOM_THEMES_KEY = "crm-custom-themes";

export const themeCategories: ThemeCategory[] = [
  { id: "featured", label: "Featured themes" },
  { id: "dark-moody", label: "Dark & moody" },
  { id: "nature-earth", label: "Nature & earth" },
  { id: "warm-inviting", label: "Warm & inviting" },
  { id: "cool-professional", label: "Cool & professional" },
];

const hexToHue = (hex: string): number => {
  const value = hex.replace("#", "");
  const r = parseInt(value.slice(0, 2), 16) / 255;
  const g = parseInt(value.slice(2, 4), 16) / 255;
  const b = parseInt(value.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;

  if (delta === 0) {
    return 0;
  }

  let hue = 0;
  if (max === r) {
    hue = ((g - b) / delta) % 6;
  } else if (max === g) {
    hue = (b - r) / delta + 2;
  } else {
    hue = (r - g) / delta + 4;
  }

  hue *= 60;
  return hue < 0 ? hue + 360 : hue;
};

export const isDualTonePalette = (colors: string[]): boolean => {
  const start = hexToHue(colors[0]);
  const end = hexToHue(colors[colors.length - 1]);
  const hueDiff = Math.min(Math.abs(start - end), 360 - Math.abs(start - end));
  return hueDiff >= 40;
};

export const buildFourStopGradient = (
  colors: string[],
  direction = "180deg",
): string => {
  const [c1, c2, c3, c4] = colors;
  return `linear-gradient(${direction}, ${c1} 0%, ${c2} 34%, ${c3} 66%, ${c4} 100%)`;
};

const preset = (
  data: Omit<WebsiteThemePreset, "primary" | "secondary" | "sidebar" | "surface" | "base"> &
    Partial<Pick<WebsiteThemePreset, "primary" | "secondary" | "sidebar" | "surface" | "base">>,
): WebsiteThemePreset => {
  const dual = isDualTonePalette(data.colors);

  return {
    primary: dual ? data.colors[0] : data.colors[3],
    secondary: dual ? data.colors[3] : data.colors[2],
    sidebar: data.colors[1],
    surface: data.colors[1],
    base: dual ? data.colors[1] : data.colors[0],
    ...data,
  };
};

export const websiteThemePresets: WebsiteThemePreset[] = [
  preset({
    id: "blue-to-orange",
    name: "Blue to Orange",
    category: "featured",
    colors: ["#095CFD", "#3B7BFD", "#FF8C35", "#FFA935"],
    description:
      "Energetic and modern. High-contrast dual tones for bold SaaS branding.",
  }),
  preset({
    id: "teal-to-coral",
    name: "Teal to Coral",
    category: "featured",
    colors: ["#0F9B8F", "#14B8A6", "#FF6B5A", "#FF8577"],
    description:
      "Fresh and vibrant. Balanced warmth with a contemporary travel feel.",
    badge: "recommended",
  }),
  preset({
    id: "navy-to-emerald",
    name: "Navy to Emerald",
    category: "featured",
    colors: ["#1A3A52", "#1F5E7A", "#2D9D78", "#3BBFA0"],
    description:
      "Professional, trustworthy. Nature-inspired. Premium CRM feel.",
  }),
  preset({
    id: "indigo-to-rose",
    name: "Indigo to Rose",
    category: "featured",
    colors: ["#4C5FD5", "#6366F1", "#E85B8A", "#F472B6"],
    description:
      "Creative and expressive. Modern gradient pairing for standout UI.",
  }),
  preset({
    id: "deep-blue-to-gold",
    name: "Deep Blue to Gold",
    category: "featured",
    colors: ["#0D47A1", "#1565C0", "#FFC107", "#FFD54F"],
    description:
      "Premium luxury travel style with elegant gold accents.",
  }),
  preset({
    id: "violet-to-coral",
    name: "Violet to Coral",
    category: "featured",
    colors: ["#7C3AED", "#8B5CF6", "#FF7043", "#FF8A65"],
    description:
      "Bold and memorable. Strong brand personality with warm highlights.",
  }),
  preset({
    id: "noir-navy",
    name: "Noir & Navy",
    category: "dark-moody",
    colors: ["#0A0A0A", "#1A1A2E", "#16213E", "#0F3460"],
    description:
      "Ultra premium, luxury travel. Dark mode native. Sophisticated, high-end feel.",
  }),
  preset({
    id: "royal-indigo",
    name: "Royal Indigo",
    category: "dark-moody",
    colors: ["#1A237E", "#283593", "#4527A0", "#6A1B9A"],
    description:
      "Regal and modern. Deep indigo to violet — unique, memorable, and elegant.",
  }),
  preset({
    id: "forest-green",
    name: "Forest Green",
    category: "nature-earth",
    colors: ["#1B5E20", "#2E7D32", "#388E3C", "#66BB6A"],
    description:
      "Eco-travel, nature trips. Fresh, organic, and calming. Great for adventure tourism.",
  }),
  preset({
    id: "walnut-mocha",
    name: "Walnut & Mocha",
    category: "nature-earth",
    colors: ["#3E2723", "#5D4037", "#795548", "#A1887F"],
    description:
      "Warm, artisanal, boutique hotel feel. Earthy and sophisticated — timeless elegance.",
  }),
  preset({
    id: "plum-to-amber",
    name: "Plum to Amber",
    category: "warm-inviting",
    colors: ["#6A1B9A", "#8E24AA", "#FFB300", "#FFC947"],
    description:
      "Inviting and rich. Deep plum warmth balanced with golden amber highlights.",
  }),
  preset({
    id: "terracotta-saffron",
    name: "Terracotta & Saffron",
    category: "warm-inviting",
    colors: ["#BF360C", "#E64A19", "#FF8F00", "#FFB300"],
    description:
      "Sun-drenched destinations. Warm terracotta with saffron accents.",
  }),
  preset({
    id: "deep-teal-cyan",
    name: "Deep Teal to Cyan",
    category: "cool-professional",
    colors: ["#00695C", "#00897B", "#00ACC1", "#26C6DA"],
    description:
      "Cool and composed. Professional teal gradient for corporate travel platforms.",
  }),
  preset({
    id: "corporate-cobalt",
    name: "Corporate Cobalt",
    category: "cool-professional",
    colors: ["#0F4C81", "#1565C0", "#1976D2", "#42A5F5"],
    description:
      "Clean, corporate, reliable travel and SaaS experience.",
  }),
  preset({
    id: "ocean-blue",
    name: "Ocean Blue",
    category: "cool-professional",
    colors: ["#0F4C81", "#1565C0", "#1976D2", "#00ACC1"],
    description:
      "Trustworthy maritime palette. Ideal for global travel operations.",
  }),
  preset({
    id: "midnight-gold",
    name: "Midnight & Gold",
    category: "cool-professional",
    colors: ["#1B2A44", "#2C4A6C", "#C49A38", "#D4AF37"],
    description:
      "Executive luxury. Deep midnight tones with refined gold accents.",
  }),
  preset({
    id: "deep-indigo",
    name: "Deep Indigo",
    category: "cool-professional",
    colors: ["#312E81", "#4338CA", "#6D28D9", "#7C3AED"],
    description:
      "Modern SaaS aesthetic with bold branding and strong visual hierarchy.",
  }),
];

export const DEFAULT_WEBSITE_THEME_ID = "blue-to-orange";

export const websiteThemePresetMap = Object.fromEntries(
  websiteThemePresets.map((item) => [item.id, item]),
) as Record<string, WebsiteThemePreset>;

export const websiteThemePresetIds = websiteThemePresets.map(
  (item) => item.id,
) as [string, ...string[]];

export const getPresetsByCategory = (
  categoryId: ThemeCategoryId,
  presets: WebsiteThemePreset[] = websiteThemePresets,
) => presets.filter((item) => item.category === categoryId);
